import { useMutation, useQueryClient, UseMutationOptions } from 'react-query'
import {
  ExternalIntegrationsClient,
  ExternalIntegration,
  LinkedInProfile,
  InstagramProfile,
  FacebookProfile,
  XProfile,
  TikTokProfile,
  WordPressProfile,
  YouTubeProfile,
  MailchimpProfile,
  MetaAdsProfile,
  GoHighLevelProfile,
  GoogleAdsAdditionalData,
} from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

type ExternalIntegrationAdditionalData =
  | Partial<LinkedInProfile>
  | Partial<InstagramProfile>
  | Partial<FacebookProfile>
  | Partial<XProfile>
  | Partial<TikTokProfile>
  | Partial<WordPressProfile>
  | Partial<YouTubeProfile>
  | Partial<MailchimpProfile>
  | Partial<MetaAdsProfile>
  | Partial<GoHighLevelProfile>
  | { default_uri?: string } // For Google integration
  | Partial<GoogleAdsAdditionalData>

interface UpdateExternalIntegrationParams {
  id: number
  additional_data?: ExternalIntegrationAdditionalData
}

type Data = { external_integration: ExternalIntegration }
type Variables = UpdateExternalIntegrationParams

export type Options = Omit<UseMutationOptions<Data, Error, Variables>, 'mutationFn' | 'mutationKey'>

export const useUpdateExternalIntegration = (options?: Options) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, Error, Variables>(
    ['updateExternalIntegration', workspaceId],
    async (params) => {
      const response = await ExternalIntegrationsClient.updateExternalIntegration({
        workspaceId,
        id: params.id,
        params: {
          additional_data: params.additional_data,
        },
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to update external integration')
      }

      return response.data
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])

        queryClient.setQueryData<{ external_integrations: ExternalIntegration[] } | undefined>(
          ['externalIntegrations', workspaceId],
          (oldData) => {
            return {
              external_integrations: (oldData?.external_integrations || []).map((integration) =>
                integration.id === data.external_integration.id ? data.external_integration : integration,
              ),
            }
          },
        )
      },
      onError: (err, variables, context) => {
        options?.onError?.(err, variables, context)
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
      },
      ...options,
    },
  )
}
